import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Input,
  Table,
  Card,
  List,
  Spin,
} from "antd";
import { Content } from "antd/es/layout/layout";
import {
  SearchOutlined,
  RightOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import DetailModal from "../Components/Transactions/DetailModal";
import {
  formatDateToLocal,
  formatNumber,
  getRecipientLabel,
  getStatusLabel,
  getTransactionTitle,
  getTypeLabel,
  isPendingStatus,
  isRejectedStatus,
} from "../Components/Transactions/helpers";
import { signedPost } from "../Services/awsSignedFetch";
import "./style.css";

const HISTORY_API =
  "https://0xh0tph3f1.execute-api.us-east-2.amazonaws.com/default/getTransactionHistory-staging";

const PAGE_SIZE = 20;

const Transactions = () => {
  const location = useLocation();
  const [txt_Search, setTxt_Search] = useState("");
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isMobile = viewportWidth < 768;

  const [transactionData, setTransactionData] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const sentinelRef = useRef(null);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const renderTypeText = (record) => (
    <span className="transactionTypeText">{getTypeLabel(record)}</span>
  );

  const renderStatusPill = (status) => {
    const label = getStatusLabel(status);
    if (isPendingStatus(status)) {
      return (
        <span className="transactionStatusPill transactionStatusPillPending">
          <ClockCircleOutlined />
          <span>{label}</span>
        </span>
      );
    }
    if (isRejectedStatus(status)) {
      return (
        <span className="transactionStatusPill transactionStatusPillRejected">
          <CloseCircleOutlined />
          <span>{label}</span>
        </span>
      );
    }
    return (
      <span className="transactionStatusPill transactionStatusPillCompleted">
        <CheckCircleOutlined />
        <span>{label}</span>
      </span>
    );
  };

  const renderAmount = (record) => (
    <span
      className={`transactionAmountValue ${
        isPendingStatus(record.status)
          ? "transactionAmountPending"
          : isRejectedStatus(record.status)
            ? "transactionAmountRejected"
            : ""
      }`}
    >
      {record.amount != null
        ? `${formatNumber(record.amount, 2)} ${record.currency || ""}`
        : "—"}
    </span>
  );

  const renderRecipientGetsAmount = (record) => (
    <span
      className={`transactionAmountValue ${
        isPendingStatus(record.status)
          ? "transactionAmountPending"
          : isRejectedStatus(record.status)
            ? "transactionAmountRejected"
            : ""
      }`}
    >
      {record.receivedAmount != null && record.targetCurrency
        ? `${formatNumber(record.receivedAmount, 2)} ${record.targetCurrency}`
        : "—"}
    </span>
  );

  // ─── Fetch ───────────────────────────────────────────────────────────────

  const fetchHistory = useCallback(async (token = null) => {
    const isFirstLoad = token === null;
    isFirstLoad ? setLoading(true) : setLoadingMore(true);

    try {
      const { lambdaStatus, data } = await signedPost(HISTORY_API, {
        limit: PAGE_SIZE,
        ...(token ? { nextToken: token } : {}),
        sort: { by: "date", order: "DESC" },
      });

      console.log("[TransactionHistory] status:", lambdaStatus, "data:", data);

      if (lambdaStatus !== 200) {
        console.error("[TransactionHistory] Lambda error:", data?.error);
        return;
      }

      setTransactionData((prev) =>
        isFirstLoad ? data.items : [...prev, ...data.items],
      );
      setTotal(data.total ?? 0);
      setNextToken(data.nextToken ?? null);
    } catch (err) {
      console.error("[TransactionHistory] fetch failed:", err);
    } finally {
      isFirstLoad ? setLoading(false) : setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(null);
  }, [fetchHistory]);

  useEffect(() => {
    const openReferenceID = location.state?.openReferenceID;
    const openTransactionID = location.state?.openTransactionID;
    if (!transactionData.length || (!openReferenceID && !openTransactionID)) {
      return;
    }

    const matched = transactionData.find(
      (record) =>
        (openTransactionID && record.id === openTransactionID) ||
        (openReferenceID && record.referenceID === openReferenceID),
    );

    if (matched) {
      setSelectedTransaction(matched);
      setModalVisible(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, transactionData]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && nextToken && !loadingMore && !txt_Search) {
          fetchHistory(nextToken);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextToken, loadingMore, txt_Search, fetchHistory]);

  // ─── Search ──────────────────────────────────────────────────────────────

  const filteredTransactions = txt_Search
    ? transactionData.filter((t) => {
        const q = txt_Search.toLowerCase();
        const name = `${t.recipientFirstName || ""} ${t.recipientLastName || ""}`.toLowerCase();
        return (
          name.includes(q) ||
          (t.referenceID || "").toLowerCase().includes(q)
        );
      })
    : transactionData;

  // ─── Modal ───────────────────────────────────────────────────────────────

  const handleDetailsClick = (record) => {
    setSelectedTransaction(record);
    setModalVisible(true);
  };

  // ─── Table Columns ───────────────────────────────────────────────────────

  const columns = [
    {
      title: "Type",
      key: "type",
      render: (_, record) => renderTypeText(record),
    },
    {
      title: "Recipient / Description",
      key: "recipient",
      render: (_, record) => getRecipientLabel(record),
    },
    {
      title: "Date",
      key: "date",
      render: (_, record) => formatDateToLocal(record.date),
    },
    {
      title: "Reference ID",
      dataIndex: "referenceID",
      key: "referenceID",
      render: (text) => text || "—",
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, record) => renderAmount(record),
    },
    {
      title: "Recipient Gets",
      key: "receivedAmount",
      render: (_, record) => renderRecipientGetsAmount(record),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => renderStatusPill(record.status),
    },
    {
      title: "",
      key: "arrow",
      width: 48,
      render: () => <RightOutlined className="transactionsRowArrow" />,
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Content className="appPageContent">
      <div className="appPageShell transactionsPageShell">
        <div className="appSectionHeader transactions-header">
          <div className="transactions-titleGroup">
            <h1 className="appSectionTitle transactions-title">Transactions</h1>
            <span className="transactions-count">({total})</span>
          </div>
          <Input
            className="appSearchInput transactions-search"
            value={txt_Search}
            onChange={(e) => setTxt_Search(e.target.value.toLowerCase())}
            placeholder="Search by recipient or reference ID"
            prefix={<SearchOutlined />}
            allowClear
          />
        </div>

        <div className="appTableCard transactionTableContainer">
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : isMobile ? (
            <div className="transactions-mobile-list">
              <List
                dataSource={filteredTransactions}
                renderItem={(record) => (
                  <Card
                    className="transaction-card"
                    onClick={() => handleDetailsClick(record)}
                  >
                    <div className="transaction-card-header">
                      <span className="transaction-card-recipient">
                        <UserOutlined /> {getTransactionTitle(record)}
                      </span>
                      {renderStatusPill(record.status)}
                    </div>
                    <div className="transaction-card-body">
                      <div className="transaction-card-row">
                        <span className="transaction-card-label">Type</span>
                        {renderTypeText(record)}
                      </div>
                      <div className="transaction-card-row">
                        <span className="transaction-card-label">Details</span>
                        <span className="transaction-card-value">
                          {getRecipientLabel(record)}
                        </span>
                      </div>
                      <div className="transaction-card-row">
                        <span className="transaction-card-label">Amount</span>
                        {renderAmount(record)}
                      </div>
                      {record.receivedAmount != null && record.targetCurrency && (
                        <div className="transaction-card-row">
                          <span className="transaction-card-label">Recipient gets</span>
                          {renderRecipientGetsAmount(record)}
                        </div>
                      )}
                      <div className="transaction-card-row">
                        <span className="transaction-card-label">Date</span>
                        <span className="transaction-card-value">
                          {formatDateToLocal(record.date)}
                        </span>
                      </div>
                      {record.referenceID && (
                        <div className="transaction-card-row">
                          <span className="transaction-card-label">Reference</span>
                          <span className="transaction-card-value transaction-card-tracking">
                            {record.referenceID}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="transaction-card-footer">
                      <span>View Details</span>
                      <RightOutlined />
                    </div>
                  </Card>
                )}
              />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredTransactions}
              rowKey={(record) => record.id || record.referenceID || Math.random()}
              pagination={false}
              className="transactions-table"
              onRow={(record) => ({
                onClick: () => handleDetailsClick(record),
                className: "transactionsClickableRow",
              })}
            />
          )}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <Spin size="small" />
            </div>
          )}
        </div>
      </div>

      <DetailModal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        selectedTransaction={selectedTransaction}
        isMobile={isMobile}
      />
    </Content>
  );
};

export default Transactions;
